import {
  getGetBrandsBrandIdAccountUrl,
  getGetV5BrandsBrandIdBoardsUrl,
  getGetV5BrandsBrandIdExportsYourPinsUrl,
  getGetV5BrandsBrandIdPinsUrl,
  getPostV5BrandsBrandIdBoardsUrl,
} from '@dashhudson/api-client/pinterest/client';
import merge from 'lodash/merge';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';

const http = createDashHttpClient(env.pinterestApiUrl);

type DateWindow = { brandId: number; startDate?: string; endDate?: string };

export function getPinterestAccounts(
  { brandIds }: { brandIds?: number[] },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/accounts`,
    merge(
      {
        params: {
          brandIds: brandIds?.join(','),
        },
      },
      config,
    ),
  );
}

export function getBoards({ brandId }: { brandId: number }, config: HttpRequestConfig = {}) {
  return http.get(getGetV5BrandsBrandIdBoardsUrl(brandId), config);
}

export function createBoard(
  { brandId, name }: { brandId: number; name: string },
  config: HttpRequestConfig = {},
) {
  return http.post(getPostV5BrandsBrandIdBoardsUrl(brandId), { name }, config);
}

export function getPinterestAccount(
  { brandId }: { brandId: number },
  config: HttpRequestConfig = {},
) {
  return http.get(
    getGetBrandsBrandIdAccountUrl(brandId),
    merge(
      {
        camelizeKeys: true,
      },
      config,
    ),
  );
}

export async function getMetrics(
  { brandId, startDate, endDate }: DateWindow,
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/v5/brands/${brandId}/metrics/account`,
    merge(
      {
        params: {
          startDate,
          endDate,
        },
      },
      config,
    ),
  );
}

export async function getPins(
  {
    brandId,
    startDate,
    endDate,
    sort,
    limit,
    privacy,
    has7dInsights,
    pinTypes,
    offset,
  }: DateWindow & {
    sort?: string;
    limit?: number;
    privacy?: string;
    has7dInsights?: boolean;
    pinTypes?: string[];
    offset?: number;
  },
  config: HttpRequestConfig = {},
) {
  return http.get(
    getGetV5BrandsBrandIdPinsUrl(brandId),
    merge(
      {
        params: {
          startDate,
          endDate,
          sort,
          limit,
          privacy,
          has7dInsights,
          pinTypes: pinTypes?.join(','),
          offset,
        },
      },
      config,
    ),
  );
}

export async function getTopPins(
  {
    brandId,
    startDate,
    endDate,
    sort,
    limit,
  }: Omit<DateWindow, 'brandId'> & {
    // Top Pins query is gated on `enabled`, which TS can't see inside the fetcher.
    brandId: number | null | undefined;
    sort?: string;
    limit?: number;
  },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/v5/brands/${brandId}/metrics/top_pins`,
    merge(
      {
        params: { startDate, endDate, sort, limit },
      },
      config,
    ),
  );
}

export async function getAnalyticsPinMetricsExport(
  {
    brandId,
    startDate,
    endDate,
    filename,
    scale,
  }: DateWindow & { filename?: string; scale?: string },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/v5/brands/${brandId}/exports/metrics/account`,
    merge(
      {
        params: {
          startDate,
          endDate,
          filename,
          scale,
        },
      },
      config,
    ),
  );
}

export async function getAnalyticsTopPinsExport(
  {
    brandId,
    startDate,
    endDate,
    filename,
    sort,
  }: DateWindow & { filename?: string; sort?: string },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/v5/brands/${brandId}/exports/metrics/top_pins`,
    merge(
      {
        params: {
          startDate,
          endDate,
          filename,
          sort,
        },
      },
      config,
    ),
  );
}

export async function getYoursPinsExport(
  {
    brandId,
    startDate,
    endDate,
    pinTypes,
    privacy,
  }: DateWindow & { pinTypes?: string[]; privacy?: string },
  config: HttpRequestConfig = {},
) {
  return http.get(
    getGetV5BrandsBrandIdExportsYourPinsUrl(brandId),
    merge(
      {
        params: {
          startDate,
          endDate,
          pinTypes: pinTypes?.join(','),
          privacy,
        },
      },
      config,
    ),
  );
}
